import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { findOwningAgentId } from "../agent/ownership.js";
import { useThailandJobsList, THAILAND_JOBS_DEFAULT_PAGE_SIZE } from "./useThailandJobsList.js";
import { listEmployers, getEmployer, getJob, createJob, updateJob, markJobReadyForMatching } from "./thailandJobsApi.js";
import "./thailandJobsJobs.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
// Jobs is no longer its own sidebar item (2026-08-05, owner) - it is an inner
// screen of Employers, so the trail says so.
const CRUMBS = ["Backoffice", "Thailand Jobs", "Employers", "Jobs"];
const EMPLOYERS_PATH = "/backoffice/thailand-jobs/employers";
const BACK_TO_EMPLOYERS_TEXT = "กลับไปหน้านายจ้าง";
const UNDER_EMPLOYER_NOTE =
  "งานทุกงานอยู่ใต้นายจ้าง — กดปุ่ม “งาน” ที่แถวของนายจ้างในหน้านายจ้าง เพื่อดูตำแหน่งงานทั้งหมดและสร้างงานใหม่ของนายจ้างรายนั้น";
const DRAWER_TITLE_ID = "tjj-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;
const VIEW_ONLY_ROLES = ["Plc", "SuperAgent"];

// Real, known-valid enum values from ThailandJobsService's own
// ValidJobStatuses/ValidDemandStatuses HashSets - hardcoded here rather
// than derived from the current page of loaded items, since a single page
// can easily miss a status value that is still a real, selectable filter.
const JOB_STATUS_OPTIONS = ["Draft", "Open", "Paused", "Closed", "Cancelled"];
const DEMAND_STATUS_OPTIONS = ["NotReady", "ReadyForMatching", "PartiallyFilled", "Filled"];
const SALARY_PERIOD_OPTIONS = ["Daily", "Monthly"];

const HERO_COPY_TEXT =
  "ฝั่งงานในไทย — จัดการงาน (Job Demand) ที่ Agent เป็นผู้ key งานเข้าระบบโดยตรง งานนี้ยังไม่ใช่การจับคู่แรงงาน ยังไม่สร้าง Assignment และยังไม่เริ่ม Tracking ไม่มี Invite Card ในฝั่งงานนี้";
const FOOTNOTE_TEXT =
  "Agent เป็นผู้ key งานเข้าระบบโดยตรงในฝั่งงานในไทยนี้ — ไม่มี Invite Card ในฝั่งงานนี้ (Invite Card ใช้เฉพาะฝั่งรับสมัครแรงงานภายใต้ Agent เท่านั้น) การทำเครื่องหมายพร้อม Matching ในหน้านี้เป็นเพียงการเปลี่ยนสถานะความพร้อมของงาน ยังไม่จับคู่แรงงาน ไม่สร้าง assignment และไม่เริ่มการติดตามอุปกรณ์";
const CREATE_JOB_WARNING = "งานนี้ยังไม่ใช่การจับคู่แรงงาน และไม่รับ workerId ใด ๆ ในขั้นตอนนี้";
const EDIT_JOB_NOTICE = "การแก้ไขนี้แก้ไขเฉพาะข้อมูลความต้องการแรงงาน (demand data) เท่านั้น ไม่สร้าง Assignment และไม่เริ่ม Tracking";
const MARK_READY_WARNING = "การทำขั้นตอนนี้ทำให้งานพร้อมเข้า Matching แต่ยังไม่จับคู่แรงงาน";
const NOT_MATCHING_TAG_TEXT = "งานนี้ยังไม่ใช่การจับคู่แรงงาน";
const PLC_SUPER_AGENT_VIEW_ONLY_TEXT =
  "PLC / Super Agent: มุมมองอ่านอย่างเดียว (view-only) สำหรับหน้านี้ — เห็นรายการและรายละเอียดได้ครบ แต่ปุ่มสร้างงาน/แก้ไขงาน/ทำเครื่องหมายพร้อม Matching จะแสดงเป็น disabled";
const READINESS_SOURCE_NOTE =
  "เช็กลิสต์นี้แสดงเงื่อนไขจริงที่ระบบใช้ตัดสินว่างานพร้อมจับคู่หรือยัง หากข้อใดยังไม่ผ่าน ปุ่มยืนยันจะยังกดไม่ได้";
const ALREADY_READY_NOTE = "งานนี้อยู่ในสถานะพร้อมเข้า Matching แล้ว";

const JOB_STATUS_BADGE_VARIANT = { Draft: "neutral", Open: "warning", Paused: "warning", Closed: "neutral", Cancelled: "danger" };
const DEMAND_STATUS_BADGE_VARIANT = { NotReady: "neutral", ReadyForMatching: "info", PartiallyFilled: "warning", Filled: "success" };
const STATUS_BADGE_VARIANT = { Active: "success", Suspended: "danger", Inactive: "neutral" };

const EMPTY_CREATE_JOB_FORM = {
  employerId: "",
  jobTitle: "",
  jobDescription: "",
  requiredWorkerCount: "",
  languageRequirementText: "",
  startDate: "",
  endDate: "",
  salaryAmount: "",
  salaryCurrency: "THB",
  salaryPeriod: "Monthly",
  benefitsText: "",
  skillRequirements: [{ skillCode: "", requiredCount: "" }],
};

function jobStatusBadgeVariant(status) {
  return JOB_STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function demandStatusBadgeVariant(status) {
  return DEMAND_STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function statusBadgeVariant(status) {
  return STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการงานนี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายละเอียดงานนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบงานนี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

function describeCreateJobError(error) {
  if (error?.validation) {
    return "กรุณากรอกนายจ้าง ชื่อตำแหน่งงาน และจำนวนที่ต้องการให้ครบถ้วน";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์สร้างงานให้นายจ้างรายนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบนายจ้างที่เลือก กรุณาลองใหม่อีกครั้ง";
  }
  if (error?.status === 409) {
    return "Agent ที่เลือกไม่ตรงกับ Agent เจ้าของนายจ้างรายนี้ กรุณาลองใหม่อีกครั้ง";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถสร้างงานได้ กรุณาลองใหม่อีกครั้ง";
}

function describeEditJobError(error) {
  if (error?.validation) {
    return "กรุณากรอกชื่อตำแหน่งงานและจำนวนที่ต้องการให้ครบถ้วน";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์แก้ไขงานนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบงานนี้ อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 409) {
    return "สถานะของงานนี้เปลี่ยนไปแล้ว กรุณารีเฟรชแล้วลองใหม่อีกครั้ง";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถบันทึกการแก้ไขได้ กรุณาลองใหม่อีกครั้ง";
}

function describeMarkReadyError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำเครื่องหมายพร้อม Matching สำหรับงานนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบงานนี้ อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 409) {
    // ApiClientError.message is always the generic "Request failed with
    // status 409" (apiClient.js never parses/attaches the response body's
    // own message) - never trust it as user-facing copy here.
    return "งานนี้ยังไม่ผ่านเงื่อนไขความพร้อม Matching กรุณาตรวจสอบเช็กลิสต์อีกครั้ง";
  }
  return "ไม่สามารถทำเครื่องหมายพร้อม Matching ได้ กรุณาลองใหม่อีกครั้ง";
}

/** Two-letter monogram from a job title - never a fabricated icon/logo. */
function monogram(title) {
  const trimmed = (title ?? "").trim();
  if (!trimmed) {
    return "-";
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

function formatDateTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

function uniqueAgents(items) {
  const seen = new Map();
  for (const item of items) {
    const agentId = item.agent?.agentId;
    if (agentId && !seen.has(agentId)) {
      seen.set(agentId, item.agent);
    }
  }
  return [...seen.values()];
}

function uniqueSuperAgents(items) {
  const seen = new Map();
  for (const item of items) {
    const id = item.line?.superAgentOrganizationId;
    if (id && !seen.has(id)) {
      seen.set(id, { id, name: item.line?.superAgentName });
    }
  }
  return [...seen.values()];
}

function uniquePlcs(items) {
  const seen = new Map();
  for (const item of items) {
    const id = item.line?.plcOrganizationId;
    if (id && !seen.has(id)) {
      seen.set(id, { id, name: item.line?.plcName });
    }
  }
  return [...seen.values()];
}

function uniqueEmployers(items) {
  const seen = new Map();
  for (const item of items) {
    if (item.employerId && !seen.has(item.employerId)) {
      seen.set(item.employerId, { id: item.employerId, name: item.employerName });
    }
  }
  return [...seen.values()];
}

/**
 * UI-derived readiness checklist (Prompt 103B) - JobDetailDto never returns
 * a conflict-reason list, only the boolean nextActions.canMarkReadyForMatching.
 * Every line here is computed from a real JobDetailDto field and mirrors one
 * of the seven preconditions named in
 * ThailandJobsService.BuildReadyForMatchingConflictReasons - never an
 * invented backend field. See the W49 UI reference README's identical
 * "Readiness checklist" section for the same sourcing note.
 */
function evaluateReadiness(detail) {
  const required = detail.requiredWorkerCount ?? 0;
  const filled = detail.filledWorkerCount ?? 0;
  const hasSkillOrRequirementText =
    (detail.skillRequirements?.length ?? 0) > 0 || Boolean(detail.languageRequirementText?.trim()) || Boolean(detail.jobDescription?.trim());

  return [
    { pass: !["Closed", "Cancelled"].includes(detail.jobStatus), label: "สถานะงานไม่ใช่ Closed หรือ Cancelled" },
    { pass: required > 0, label: "จำนวนที่ต้องการมากกว่า 0" },
    { pass: required > filled, label: "จำนวนที่ต้องการมากกว่าจำนวนที่กรอกแล้ว (ยังมีอัตราว่าง)" },
    { pass: detail.employer?.employerStatus === "Active", label: "นายจ้างอยู่ในสถานะ Active และมองเห็นได้" },
    { pass: hasSkillOrRequirementText, label: "มีทักษะที่ต้องการ ภาษาที่ต้องการ หรือรายละเอียดงานระบุไว้" },
  ];
}

/**
 * /backoffice/thailand-jobs/jobs - the Thailand Jobs Jobs screen (Prompt
 * 103B, built from the W49 reference). Read-heavy list/detail, Create Job,
 * Edit Job, and a real Mark Ready For Matching action over the Prompt 101
 * API. This is job-supply-side data intake only - it never calls a
 * matching/assignment/tracking/Invite-Card API (none of those exist for
 * this side of the platform), and no form on this page ever collects or
 * sends a workerId.
 *
 * Since 2026-08-05 this screen has no sidebar entry of its own (owner: "job
 * ให้รวมเข้าที่ employer เลยครับ ไม่ต้องแยกอีกเมนูครับ") - it is an inner screen
 * of Employers, reached from an employer's งาน tab via ?employerId=/?jobId=,
 * from the Dashboard's job metrics, or by an existing bookmark. The route,
 * every control on it, and the bare no-params behaviour are unchanged; what
 * this packet added is the deep links, the breadcrumb naming Employers, and
 * the way back up. Creating a job from here still works and is still real -
 * it simply is no longer the only place to do it, and no longer the
 * discoverable one.
 */
export default function ThailandJobsJobsPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;
  const ownAgentId = useMemo(() => findOwningAgentId(identity?.accessScopes), [identity]);
  const isViewOnly = VIEW_ONLY_ROLES.includes(primaryRole);

  // ?employerId= / ?jobId= are how the Employers drawer hands off to this
  // screen now that Jobs has no sidebar entry of its own: the employer's งาน
  // tab links here pre-filtered to that employer, and a job row links straight
  // to that job's detail drawer. Both are plain, optional deep links - opening
  // /backoffice/thailand-jobs/jobs bare still behaves exactly as before.
  const [searchParams] = useSearchParams();
  const employerIdParam = searchParams.get("employerId") ?? "";
  const jobIdParam = searchParams.get("jobId") ?? "";

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [jobStatusFilter, setJobStatusFilter] = useState("");
  const [demandStatusFilter, setDemandStatusFilter] = useState("");
  const [employerFilter, setEmployerFilter] = useState(employerIdParam);
  const [agentFilter, setAgentFilter] = useState("");
  const [superAgentFilter, setSuperAgentFilter] = useState("");
  const [plcFilter, setPlcFilter] = useState("");
  const [skillFilter, setSkillFilter] = useState("");
  const [page, setPage] = useState(1);

  const [selectedJobId, setSelectedJobId] = useState(jobIdParam || null);
  const [drawerView, setDrawerView] = useState(jobIdParam ? "detail" : null); // null | "detail" | "mark-ready" | "create-job" | "edit-job"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");

  const [employerOptions, setEmployerOptions] = useState({ status: "idle", data: [] });
  const [createJobForm, setCreateJobForm] = useState(EMPTY_CREATE_JOB_FORM);
  const [createJobEmployerDetail, setCreateJobEmployerDetail] = useState({ status: "idle", data: null });
  const [createJobSubmit, setCreateJobSubmit] = useState({ status: "idle", error: null });

  const [editJobForm, setEditJobForm] = useState(null);
  const [editJobSubmit, setEditJobSubmit] = useState({ status: "idle", error: null });

  const [markReadySubmit, setMarkReadySubmit] = useState({ status: "idle", error: null });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, jobStatusFilter, demandStatusFilter, employerFilter, agentFilter, superAgentFilter, plcFilter, skillFilter]);

  // Deep-link params can change while this screen stays mounted (browser
  // back/forward between two jobs of the same employer, for instance). These
  // only run when the URL itself changes, so they never fight a filter the
  // visitor picked by hand or a drawer they closed.
  useEffect(() => {
    setEmployerFilter(employerIdParam);
  }, [employerIdParam]);

  useEffect(() => {
    if (jobIdParam) {
      setSelectedJobId(jobIdParam);
      setDrawerView("detail");
    }
  }, [jobIdParam]);

  useEffect(() => {
    setActiveTab("overview");
  }, [selectedJobId]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: THAILAND_JOBS_DEFAULT_PAGE_SIZE,
      search,
      jobStatus: jobStatusFilter,
      demandStatus: demandStatusFilter,
      employerId: employerFilter,
      agentId: agentFilter,
      superAgentOrganizationId: superAgentFilter,
      plcOrganizationId: plcFilter,
      skillCode: skillFilter,
    }),
    [page, search, jobStatusFilter, demandStatusFilter, employerFilter, agentFilter, superAgentFilter, plcFilter, skillFilter]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useThailandJobsList(filters);

  // Detail is fetched whenever selectedJobId changes (row/card opened, or a
  // create-job success moves selection to the new job) - it stays cached
  // across mark-ready/edit-job sub-views for the same job, and only
  // refetches for the same id when a mark-ready/edit-job succeeds
  // (detailReloadToken).
  useEffect(() => {
    if (!selectedJobId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedJobId ? previous.data : null,
      error: null,
      forId: selectedJobId,
    }));

    getJob(selectedJobId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedJobId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedJobId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedJobId
  }, [selectedJobId, detailReloadToken]);

  function openJobDetail(jobId) {
    setSelectedJobId(jobId);
    setDrawerView("detail");
  }

  function ensureEmployerOptionsLoaded() {
    if (employerOptions.status !== "idle") {
      return;
    }
    setEmployerOptions({ status: "loading", data: [] });
    listEmployers({ page: 1, pageSize: 100 })
      .then((data) => setEmployerOptions({ status: "ready", data: data.items ?? [] }))
      .catch(() => setEmployerOptions({ status: "error", data: [] }));
  }

  function openCreateJobDrawer() {
    setCreateJobForm(EMPTY_CREATE_JOB_FORM);
    setCreateJobEmployerDetail({ status: "idle", data: null });
    setCreateJobSubmit({ status: "idle", error: null });
    ensureEmployerOptionsLoaded();
    setDrawerView("create-job");
  }

  function openEditJobDrawer() {
    const detail = detailState.data;
    if (!detail) {
      return;
    }
    setEditJobForm({
      jobTitle: detail.jobTitle ?? "",
      jobDescription: detail.jobDescription ?? "",
      requiredWorkerCount: String(detail.requiredWorkerCount ?? ""),
      languageRequirementText: detail.languageRequirementText ?? "",
      startDate: detail.startDate ?? "",
      endDate: detail.endDate ?? "",
      salaryAmount: detail.salaryAmount != null ? String(detail.salaryAmount) : "",
      salaryCurrency: detail.salaryCurrency ?? "THB",
      salaryPeriod: detail.salaryPeriod ?? "Monthly",
      benefitsText: detail.benefitsText ?? "",
      skillRequirements:
        detail.skillRequirements && detail.skillRequirements.length > 0
          ? detail.skillRequirements.map((r) => ({
              requirementId: r.requirementId ?? null,
              skillCode: r.skillCode ?? "",
              requiredCount: r.requiredCount != null ? String(r.requiredCount) : "",
            }))
          : [{ requirementId: null, skillCode: "", requiredCount: "" }],
    });
    setEditJobSubmit({ status: "idle", error: null });
    setDrawerView("edit-job");
  }

  function openMarkReadyDrawer() {
    setMarkReadySubmit({ status: "idle", error: null });
    setDrawerView("mark-ready");
  }

  function backToDetail() {
    setDrawerView("detail");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedJobId(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedJobId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  function updateCreateJobField(field, value) {
    setCreateJobForm((previous) => ({ ...previous, [field]: value }));
  }

  function handleCreateJobEmployerChange(employerId) {
    updateCreateJobField("employerId", employerId);
    if (!employerId) {
      setCreateJobEmployerDetail({ status: "idle", data: null });
      return;
    }
    setCreateJobEmployerDetail({ status: "loading", data: null });
    getEmployer(employerId)
      .then((data) => setCreateJobEmployerDetail({ status: "ready", data }))
      .catch(() => setCreateJobEmployerDetail({ status: "error", data: null }));
  }

  function updateEditJobField(field, value) {
    setEditJobForm((previous) => ({ ...previous, [field]: value }));
  }

  function updateSkillRow(formSetter, index, field, value) {
    formSetter((previous) => ({
      ...previous,
      skillRequirements: previous.skillRequirements.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  }

  function addSkillRow(formSetter) {
    formSetter((previous) => ({
      ...previous,
      skillRequirements: [...previous.skillRequirements, { skillCode: "", requiredCount: "" }],
    }));
  }

  async function handleCreateJobSubmit(event) {
    event.preventDefault();
    const requiredWorkerCount = Number(createJobForm.requiredWorkerCount);
    const agentId = createJobEmployerDetail.data?.agent?.agentId;
    if (!createJobForm.employerId || !createJobForm.jobTitle.trim() || !createJobForm.requiredWorkerCount.trim() || !Number.isFinite(requiredWorkerCount) || !agentId) {
      setCreateJobSubmit({ status: "error", error: { validation: true } });
      return;
    }
    setCreateJobSubmit({ status: "submitting", error: null });
    try {
      const created = await createJob({
        agentId,
        employerId: createJobForm.employerId,
          jobTitle: createJobForm.jobTitle.trim(),
        jobDescription: createJobForm.jobDescription.trim() || null,
        requiredWorkerCount,
        startDate: createJobForm.startDate.trim() || null,
        endDate: createJobForm.endDate.trim() || null,
        salaryAmount: createJobForm.salaryAmount.trim() ? Number(createJobForm.salaryAmount) : null,
        salaryCurrency: createJobForm.salaryCurrency.trim() || null,
        salaryPeriod: createJobForm.salaryPeriod || null,
        benefitsText: createJobForm.benefitsText.trim() || null,
        languageRequirementText: createJobForm.languageRequirementText.trim() || null,
        skillRequirements: createJobForm.skillRequirements
          .filter((row) => row.skillCode.trim())
          .map((row) => ({
            skillCode: row.skillCode.trim(),
            requiredCount: row.requiredCount.trim() ? Number(row.requiredCount) : null,
          })),
      });
      setCreateJobSubmit({ status: "success", error: null });
      refetchList();
      setSelectedJobId(created.jobId);
      setDrawerView("detail");
    } catch (error) {
      setCreateJobSubmit({ status: "error", error });
    }
  }

  async function handleEditJobSubmit(event) {
    event.preventDefault();
    const requiredWorkerCount = Number(editJobForm.requiredWorkerCount);
    if (!editJobForm.jobTitle.trim() || !editJobForm.requiredWorkerCount.trim() || !Number.isFinite(requiredWorkerCount) || !selectedJobId) {
      setEditJobSubmit({ status: "error", error: { validation: true } });
      return;
    }
    setEditJobSubmit({ status: "submitting", error: null });
    try {
      await updateJob(selectedJobId, {
        jobTitle: editJobForm.jobTitle.trim(),
        jobDescription: editJobForm.jobDescription.trim() || null,
          requiredWorkerCount,
        startDate: editJobForm.startDate.trim() || null,
        endDate: editJobForm.endDate.trim() || null,
        salaryAmount: editJobForm.salaryAmount.trim() ? Number(editJobForm.salaryAmount) : null,
        salaryCurrency: editJobForm.salaryCurrency.trim() || null,
        salaryPeriod: editJobForm.salaryPeriod || null,
        benefitsText: editJobForm.benefitsText.trim() || null,
        languageRequirementText: editJobForm.languageRequirementText.trim() || null,
        skillRequirements: editJobForm.skillRequirements
          .filter((row) => row.skillCode.trim())
          .map((row) => ({
            requirementId: row.requirementId ?? null,
            skillCode: row.skillCode.trim(),
            requiredCount: row.requiredCount.trim() ? Number(row.requiredCount) : null,
          })),
      });
      setEditJobSubmit({ status: "success", error: null });
      refetchList();
      setDetailReloadToken((token) => token + 1);
      setDrawerView("detail");
    } catch (error) {
      setEditJobSubmit({ status: "error", error });
    }
  }

  async function handleConfirmMarkReady() {
    if (!selectedJobId) {
      return;
    }
    setMarkReadySubmit({ status: "submitting", error: null });
    try {
      await markJobReadyForMatching(selectedJobId);
      setMarkReadySubmit({ status: "success", error: null });
      refetchList();
      setDetailReloadToken((token) => token + 1);
      setDrawerView("detail");
    } catch (error) {
      setMarkReadySubmit({ status: "error", error });
    }
  }

  const items = listData?.items ?? [];
  const summary = listData?.summary ?? null;
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / THAILAND_JOBS_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(
    search || jobStatusFilter || demandStatusFilter || employerFilter || agentFilter || superAgentFilter || plcFilter || skillFilter
  );

  const agentOptions = useMemo(() => uniqueAgents(items), [items]);
  const superAgentOptions = useMemo(() => uniqueSuperAgents(items), [items]);
  const plcOptions = useMemo(() => uniquePlcs(items), [items]);
  const employerFilterOptions = useMemo(() => uniqueEmployers(items), [items]);

  // Opened from an employer's drawer: name that employer from the loaded rows
  // themselves - never a fabricated label. If the filtered page came back
  // empty (or the employer's jobs are all on a later page), the id is still
  // carried as a real, selected option so the select can never show "ทั้งหมด"
  // while a filter is in fact applied.
  const employerContextName = useMemo(
    () => (employerFilter ? employerFilterOptions.find((option) => option.id === employerFilter)?.name ?? null : null),
    [employerFilter, employerFilterOptions]
  );

  const employerSelectOptions = useMemo(() => {
    if (!employerFilter || employerFilterOptions.some((option) => option.id === employerFilter)) {
      return employerFilterOptions;
    }
    return [{ id: employerFilter, name: "นายจ้างที่เลือกไว้จากหน้านายจ้าง" }, ...employerFilterOptions];
  }, [employerFilter, employerFilterOptions]);

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดรายการงาน..." />
      </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการงานได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={handleRefresh}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-tjj-page">
        <div className="wx-tjj-toolbar">
          <label className="wx-tjj-search">
            <span className="wx-visually-hidden">ค้นหาชื่องาน นายจ้าง หรือทักษะ</span>
            <input
              type="search"
              placeholder="ค้นหาชื่องาน นายจ้าง หรือทักษะ"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-tjj-filters">
            <label className="wx-tjj-filter">
              <span>Agent</span>
              <select value={agentFilter} onChange={(event) => setAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {agentOptions.map((agent) => (
                  <option key={agent.agentId} value={agent.agentId}>
                    {agent.agentCode} · {agent.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-tjj-filter">
              <span>Super Agent</span>
              <select value={superAgentFilter} onChange={(event) => setSuperAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {superAgentOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-tjj-filter">
              <span>PLC / บมจ</span>
              <select value={plcFilter} onChange={(event) => setPlcFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {plcOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-tjj-filter">
              <span>นายจ้าง</span>
              <select value={employerFilter} onChange={(event) => setEmployerFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {employerSelectOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-tjj-filter">
              <span>สถานะงาน</span>
              <select value={jobStatusFilter} onChange={(event) => setJobStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {JOB_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-tjj-filter">
              <span>สถานะความพร้อม</span>
              <select value={demandStatusFilter} onChange={(event) => setDemandStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {DEMAND_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-tjj-filter">
              <span>ทักษะ</span>
              <input type="text" placeholder="เช่น SEWING" value={skillFilter} onChange={(event) => setSkillFilter(event.target.value)} />
            </label>
          </div>

          <div className="wx-tjj-toolbar-actions">
            {/* wx-tjj-refresh-button/wx-tjj-create-job-button are also listed
                in DetailDrawer's selectableSelector below - without them,
                clicking either while the drawer is open is treated as an
                "outside click" as far as the drawer's own close-on-outside-
                click listener is concerned (see ../components/DetailDrawer.jsx). */}
            <button type="button" className="wx-button wx-button--ghost wx-tjj-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
            <button
              type="button"
              className="wx-button wx-button--primary wx-tjj-create-job-button"
              disabled={isViewOnly}
              title={isViewOnly ? PLC_SUPER_AGENT_VIEW_ONLY_TEXT : undefined}
              onClick={openCreateJobDrawer}
            >
              + สร้างงาน
            </button>
          </div>
        </div>

        <header className="wx-tjj-head">
          <div className="wx-tjj-title">
            <Link className="wx-tjj-back-link" to={EMPLOYERS_PATH}>
              ← {BACK_TO_EMPLOYERS_TEXT}
            </Link>
            <h1>
              Jobs <span className="wx-tjj-title-th">งาน</span>
              {employerContextName && <span className="wx-tjj-title-context"> · {employerContextName}</span>}
            </h1>
            <p>{HERO_COPY_TEXT}</p>
            <p className="wx-tjj-under-employer-note">{UNDER_EMPLOYER_NOTE}</p>
          </div>
          {primaryRole && (
            <span className="wx-tjj-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-tjj-metrics">
          <MetricCard label="Open Jobs" value={summary?.openJobCount} note="ตำแหน่งงานสถานะ Open" variant="amber" />
          <MetricCard label="Required Workers" value={summary?.totalDemandCount} note="ผลรวมจำนวนที่ต้องการจากทุกงาน" />
          <MetricCard label="Filled Workers" value={summary?.filledDemandCount} note="ผลรวมจำนวนที่กรอกแล้ว" variant="green" />
          <MetricCard label="Remaining Workers" value={summary?.remainingDemandCount} note="ผลรวมจำนวนที่ยังขาดอยู่" />
          <MetricCard label="Ready For Matching Jobs" value={summary?.readyForMatchingJobCount} note="งานที่พร้อมเข้า Matching แล้ว" variant="blue" />
        </div>

        <section className="wx-panel wx-tjj-list-panel">
          <div className="wx-tjj-list-head">
            <h2 className="wx-panel-title">รายการงาน</h2>
            <span className="wx-tjj-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-tjj-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มีงานในขอบเขตที่มองเห็น"}</p>
          ) : (
            <>
              <div className="wx-tjj-table-wrap">
                <table className="wx-tjj-table">
                  <colgroup>
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "8%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Employer</th>
                      <th>Agent / Line</th>
                      <th>Required / Filled / Remaining</th>
                      <th>Skills</th>
                      <th>Job Status</th>
                      <th>Demand Status</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.jobId} className="wx-tjj-row" onClick={() => openJobDetail(item.jobId)}>
                        <td>
                          <div className="wx-tjj-job-cell">
                            <span className="wx-tjj-avatar" aria-hidden="true">
                              {monogram(item.jobTitle)}
                            </span>
                            <div>
                              <span className="wx-tjj-cell-strong">{item.jobTitle || "-"}</span>
                              <span className="wx-tjj-cell-sub">{item.jobCode}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="wx-tjj-cell-strong">{item.employerName ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-tjj-cell-strong">
                            {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
                          </span>
                          <span className="wx-tjj-cell-sub">
                            {item.line?.superAgentName ?? "-"} · {item.line?.plcName ?? "-"}
                          </span>
                        </td>
                        <td>
                          <span className="wx-tjj-cell-strong">
                            {item.requiredWorkerCount} / {item.filledWorkerCount} / {item.remainingWorkerCount}
                          </span>
                          <span className="wx-tjj-cell-sub">ต้องการ / กรอกแล้ว / เหลือ</span>
                        </td>
                        <td>
                          <span className="wx-tjj-cell-sub">{item.skillSummary || "ไม่มี"}</span>
                        </td>
                        <td>
                          <span className={`wx-badge wx-badge--${jobStatusBadgeVariant(item.jobStatus)}`}>{item.jobStatus}</span>
                        </td>
                        <td>
                          <span className={`wx-badge wx-badge--${demandStatusBadgeVariant(item.demandStatus)}`}>{item.demandStatus}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="wx-tjj-row-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openJobDetail(item.jobId);
                            }}
                          >
                            เปิดรายละเอียด
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-tjj-cards">
                {items.map((item) => (
                  <JobCard key={item.jobId} item={item} onOpen={() => openJobDetail(item.jobId)} />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>

        <p className="wx-tjj-footnote">{FOOTNOTE_TEXT}</p>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-tjj-row, .wx-tjj-card, .wx-tjj-refresh-button, .wx-tjj-create-job-button"
      >
        {drawerView === "detail" && (
          <JobDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            primaryRole={primaryRole}
            onOpenEditJob={openEditJobDrawer}
            onOpenMarkReady={openMarkReadyDrawer}
          />
        )}
        {drawerView === "mark-ready" && (
          <MarkReadyView
            titleId={DRAWER_TITLE_ID}
            detail={detailState.data}
            onCancel={backToDetail}
            onConfirm={handleConfirmMarkReady}
            submitState={markReadySubmit}
          />
        )}
        {drawerView === "create-job" && (
          <CreateJobView
            titleId={DRAWER_TITLE_ID}
            form={createJobForm}
            onChange={updateCreateJobField}
            onEmployerChange={handleCreateJobEmployerChange}
            employerOptions={employerOptions}
            employerDetail={createJobEmployerDetail}
            onSkillChange={(index, field, value) => updateSkillRow(setCreateJobForm, index, field, value)}
            onAddSkillRow={() => addSkillRow(setCreateJobForm)}
            onSubmit={handleCreateJobSubmit}
            onCancel={closeDrawer}
            submitState={createJobSubmit}
          />
        )}
        {drawerView === "edit-job" && editJobForm && (
          <EditJobView
            titleId={DRAWER_TITLE_ID}
            job={detailState.data}
            form={editJobForm}
            onChange={updateEditJobField}
            onSkillChange={(index, field, value) => updateSkillRow(setEditJobForm, index, field, value)}
            onAddSkillRow={() => addSkillRow(setEditJobForm)}
            onSubmit={handleEditJobSubmit}
            onBack={backToDetail}
            submitState={editJobSubmit}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

function MetricCard({ label, value, note, variant, reserved, reservedNote, reservedValue }) {
  if (reserved) {
    return (
      <div className="wx-tjj-metric wx-tjj-metric--reserved">
        <span className="wx-tjj-metric-label">{label}</span>
        <strong className="wx-tjj-metric-value wx-tjj-metric-value--reserved">{reservedValue ?? "-"}</strong>
        <span className="wx-tjj-metric-reserved-pill">{reservedNote}</span>
      </div>
    );
  }
  const variantClass = variant ? ` wx-tjj-metric--${variant}` : "";
  return (
    <div className={`wx-tjj-metric${variantClass}`}>
      <span className="wx-tjj-metric-label">{label}</span>
      <strong className="wx-tjj-metric-value">{value ?? "-"}</strong>
      {note && <span className="wx-tjj-metric-note">{note}</span>}
    </div>
  );
}

function JobCard({ item, onOpen }) {
  return (
    <div className="wx-tjj-card" onClick={onOpen}>
      <div className="wx-tjj-card-top">
        <div className="wx-tjj-job-cell">
          <span className="wx-tjj-avatar" aria-hidden="true">
            {monogram(item.jobTitle)}
          </span>
          <div>
            <strong>{item.jobTitle || "-"}</strong>
            <span>{item.jobCode}</span>
          </div>
        </div>
        <span className={`wx-badge wx-badge--${jobStatusBadgeVariant(item.jobStatus)}`}>{item.jobStatus}</span>
      </div>
      <div className="wx-tjj-card-grid">
        <div>
          <label>นายจ้าง</label>
          <strong>{item.employerName ?? "-"}</strong>
        </div>
        <div>
          <label>Agent</label>
          <strong>
            {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
          </strong>
        </div>
        <div>
          <label>สายงาน</label>
          <strong>
            {item.line?.superAgentName ?? "-"} · {item.line?.plcName ?? "-"}
          </strong>
        </div>
        <div>
          <label>ต้องการ/กรอกแล้ว/เหลือ</label>
          <strong>
            {item.requiredWorkerCount} / {item.filledWorkerCount} / {item.remainingWorkerCount}
          </strong>
        </div>
        <div>
          <label>ทักษะ</label>
          <strong>{item.skillSummary || "ไม่มี"}</strong>
        </div>
        <div>
          <label>สถานะงาน</label>
          <strong>{item.jobStatus}</strong>
        </div>
        <div>
          <label>สถานะความพร้อม</label>
          <strong>{item.demandStatus}</strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-tjj-row-action wx-tjj-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        เปิดรายละเอียด
      </button>
    </div>
  );
}

function JobDetailView({ titleId, state, activeTab, onTabChange, primaryRole, onOpenEditJob, onOpenMarkReady }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-tjj-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const nextActions = detail.nextActions ?? {};
  const isViewOnly = VIEW_ONLY_ROLES.includes(primaryRole);
  const canEditJob = nextActions.canEditJob === true && !isViewOnly;
  const alreadyReady = detail.demandStatus === "ReadyForMatching";
  const canMarkReady = nextActions.canMarkReadyForMatching === true && !isViewOnly && !alreadyReady;

  return (
    <div className="wx-tjj-drawer">
      <span className="wx-tjj-drawer-kicker">JOB DETAIL</span>
      <h2 id={titleId} className="wx-tjj-drawer-title">
        รายละเอียดงาน
      </h2>
      <p className="wx-tjj-drawer-sub">
        {detail.jobCode} · สร้างเมื่อ {formatDateTimeTh(detail.createdAtUtc) ?? "-"}
      </p>
      <div className="wx-tjj-drawer-status">
        <span className={`wx-badge wx-badge--${jobStatusBadgeVariant(detail.jobStatus)}`}>{detail.jobStatus}</span>
        <span className={`wx-badge wx-badge--${demandStatusBadgeVariant(detail.demandStatus)}`}>{detail.demandStatus}</span>
        <span className="wx-badge wx-badge--danger">ไม่มี Invite Card ในฝั่งงานนี้</span>
      </div>

      <div className="wx-tjj-drawer-tabs">
        <button type="button" className={`wx-tjj-drawer-tab${activeTab === "overview" ? " wx-tjj-drawer-tab--active" : ""}`} onClick={() => onTabChange("overview")}>
          ภาพรวมงาน
        </button>
        <button type="button" className={`wx-tjj-drawer-tab${activeTab === "parties" ? " wx-tjj-drawer-tab--active" : ""}`} onClick={() => onTabChange("parties")}>
          นายจ้าง/Agent
        </button>
        <button type="button" className={`wx-tjj-drawer-tab${activeTab === "skills" ? " wx-tjj-drawer-tab--active" : ""}`} onClick={() => onTabChange("skills")}>
          ทักษะที่ต้องการ
        </button>
        <button type="button" className={`wx-tjj-drawer-tab${activeTab === "readiness" ? " wx-tjj-drawer-tab--active" : ""}`} onClick={() => onTabChange("readiness")}>
          ความพร้อม/ขั้นตอนถัดไป
        </button>
      </div>

      {activeTab === "overview" && <OverviewPane detail={detail} canEditJob={canEditJob} onOpenEditJob={onOpenEditJob} />}
      {activeTab === "parties" && <PartiesPane detail={detail} />}
      {activeTab === "skills" && <SkillsPane detail={detail} />}
      {activeTab === "readiness" && <ReadinessPane detail={detail} canMarkReady={canMarkReady} alreadyReady={alreadyReady} onOpenMarkReady={onOpenMarkReady} />}
    </div>
  );
}

function OverviewPane({ detail, canEditJob, onOpenEditJob }) {
  return (
    <>
      <section className="wx-tjj-dsec wx-tjj-dsec-head-row">
        <h3>ข้อมูลงาน</h3>
        <button
          type="button"
          className="wx-button wx-button--ghost wx-tjj-dsec-action"
          disabled={!canEditJob}
          title={canEditJob ? undefined : "คุณไม่มีสิทธิ์แก้ไขงานนี้"}
          onClick={onOpenEditJob}
        >
          แก้ไขงาน
        </button>
      </section>
      <section className="wx-tjj-dsec">
        <div className="wx-tjj-job-title-block">
          <span className="wx-tjj-avatar wx-tjj-avatar--lg" aria-hidden="true">
            {monogram(detail.jobTitle)}
          </span>
          <div className="wx-tjj-job-title-block-text">
            <strong>{detail.jobTitle || "-"}</strong>
            <span>{detail.jobCode} · Job Demand</span>
          </div>
        </div>
        {detail.jobDescription && <p className="wx-tjj-job-desc">{detail.jobDescription}</p>}
        <div className="wx-tjj-kv-row">
          <span>จำนวนที่ต้องการ</span>
          <strong>{detail.requiredWorkerCount} อัตรา</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>จำนวนที่กรอกแล้ว</span>
          <strong>{detail.filledWorkerCount} อัตรา</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>จำนวนที่เหลือ</span>
          <strong>{detail.remainingWorkerCount} อัตรา</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>วันที่เริ่มงาน</span>
          <strong>{formatDateTh(detail.startDate) ?? "ไม่ระบุ"}</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>วันที่สิ้นสุด</span>
          <strong className="wx-tjj-reserved-text">{formatDateTh(detail.endDate) ?? "ไม่ระบุ"}</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>ค่าจ้าง</span>
          <strong>
            {detail.salaryAmount != null ? `${detail.salaryAmount} ${detail.salaryCurrency ?? ""} / ${detail.salaryPeriod ?? "-"}` : "ไม่ระบุ"}
          </strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>สวัสดิการ</span>
          <strong>{detail.benefitsText || "ไม่ระบุ"}</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>ภาษาที่ต้องการ</span>
          <strong>{detail.languageRequirementText || "ไม่ระบุ"}</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>สถานะงาน</span>
          <strong>{detail.jobStatus}</strong>
        </div>
      </section>
      <p className="wx-tjj-note-muted wx-tjj-note-muted--left">งานนี้เป็นเพียงความต้องการแรงงาน (job demand) {NOT_MATCHING_TAG_TEXT}</p>
    </>
  );
}

function PartiesPane({ detail }) {
  return (
    <>
      <section className="wx-tjj-dsec">
        <h3>นายจ้าง</h3>
        <div className="wx-tjj-kv-row">
          <span>ชื่อนายจ้าง</span>
          <strong>{detail.employer?.employerName ?? detail.employerName ?? "-"}</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>รหัสนายจ้าง</span>
          <strong className="wx-tjj-code">{detail.employer?.employerId ?? detail.employerId ?? "-"}</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>สถานะนายจ้าง</span>
          <strong>{detail.employer?.employerStatus ?? "-"}</strong>
        </div>
      </section>
      <section className="wx-tjj-dsec">
        <h3>Agent เจ้าของและสายงาน</h3>
        <div className="wx-tjj-kv-row">
          <span>Agent เจ้าของ</span>
          <strong>
            {detail.agent?.agentCode ?? "-"} · {detail.agent?.displayName ?? "-"}
          </strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>Super Agent</span>
          <strong>{detail.line?.superAgentName ?? "-"}</strong>
        </div>
        <div className="wx-tjj-kv-row">
          <span>PLC / บมจ</span>
          <strong>{detail.line?.plcName ?? "-"}</strong>
        </div>
      </section>
    </>
  );
}

function SkillsPane({ detail }) {
  const skillRequirements = detail.skillRequirements ?? [];
  return (
    <section className="wx-tjj-dsec">
      <h3>ทักษะที่ต้องการ ({skillRequirements.length})</h3>
      {skillRequirements.length === 0 ? (
        <p className="wx-state-body">ยังไม่มีทักษะที่ต้องการสำหรับงานนี้</p>
      ) : (
        <div className="wx-tjj-doc-list">
          {skillRequirements.map((requirement) => (
            <div className="wx-tjj-doc-item" key={requirement.requirementId ?? requirement.skillCode}>
              <div className="wx-tjj-doc-item-name">
                <strong>{requirement.skillCode}</strong>
                <span>
                  {requirement.skillName ?? "-"} · ระดับขั้นต่ำ {requirement.minimumLevel ?? "ไม่ระบุ"}
                </span>
              </div>
              <span className="wx-tjj-doc-value">{requirement.requiredCount != null ? `${requirement.requiredCount} อัตรา` : "-"}</span>
            </div>
          ))}
        </div>
      )}
      <p className="wx-tjj-note-muted wx-tjj-note-muted--left">
        รหัสทักษะ ระดับขั้นต่ำ และจำนวนที่ต้องการต่อทักษะ ตามที่นายจ้างระบุไว้ในงานนี้
      </p>
    </section>
  );
}

function ReadinessPane({ detail, canMarkReady, alreadyReady, onOpenMarkReady }) {
  const checklist = evaluateReadiness(detail);
  const allPass = checklist.every((item) => item.pass);
  return (
    <>
      <section className="wx-tjj-dsec">
        <h3>เช็กลิสต์ความพร้อม Matching</h3>
        <p className="wx-tjj-dsec-sub">{READINESS_SOURCE_NOTE}</p>
        <ul className="wx-tjj-checklist">
          {checklist.map((item) => (
            <li key={item.label} className={item.pass ? "wx-tjj-check-pass" : "wx-tjj-check-fail"}>
              <span className="wx-tjj-check-ico" aria-hidden="true">
                {item.pass ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                )}
              </span>
              {item.label}
            </li>
          ))}
        </ul>
        {alreadyReady ? (
          <div className="wx-tjj-notice">{ALREADY_READY_NOTE}</div>
        ) : allPass ? (
          <div className="wx-tjj-notice">พร้อมเข้า Matching — งานนี้ผ่านทุกเงื่อนไขแล้ว</div>
        ) : (
          <div className="wx-tjj-notice wx-tjj-notice--warn">ยังไม่พร้อมเข้า Matching — งานนี้ยังไม่ผ่านเงื่อนไขบางข้อ</div>
        )}
      </section>
      <section className="wx-tjj-dsec">
        <h3>ขั้นตอนถัดไป</h3>
        <button
          type="button"
          className="wx-button wx-button--primary wx-tjj-mark-ready-button"
          disabled={!canMarkReady}
          title={canMarkReady ? undefined : "คุณไม่มีสิทธิ์ หรืองานนี้ยังไม่ผ่านเงื่อนไขความพร้อม Matching"}
          onClick={onOpenMarkReady}
        >
          ทำเครื่องหมายพร้อม Matching
        </button>
        {/* Always disabled, no onClick at all - Matching/Assignment/Tracking
            are not implemented anywhere in this codebase yet, regardless of
            what any nextActions value says. */}
        <button type="button" className="wx-button wx-tjj-reserved-button" disabled>
          เริ่ม Matching
          <small>ยังไม่เริ่ม Matching — ยังไม่เปิดใช้งาน รอแพ็กเก็ตถัดไป</small>
        </button>
        <button type="button" className="wx-button wx-tjj-reserved-button" disabled>
          สร้าง Assignment
          <small>ระบบยังไม่ผูกแรงงานเข้ากับงานนี้</small>
        </button>
        <button type="button" className="wx-button wx-tjj-reserved-button" disabled>
          เริ่มติดตามอุปกรณ์
          <small>ยังไม่เริ่ม Tracking — ยังไม่มีการติดตามอุปกรณ์ในระบบวันนี้</small>
        </button>
      </section>
    </>
  );
}

function MarkReadyView({ titleId, detail, onCancel, onConfirm, submitState }) {
  const submitting = submitState.status === "submitting";
  if (!detail) {
    return null;
  }
  return (
    <div className="wx-tjj-drawer">
      <span className="wx-tjj-drawer-kicker">MARK READY FOR MATCHING</span>
      <h2 id={titleId} className="wx-tjj-drawer-title">
        ยืนยันทำเครื่องหมายพร้อม Matching
      </h2>
      <p className="wx-tjj-drawer-sub">ตรวจสอบข้อมูลก่อนยืนยัน</p>
      <div className="wx-tjj-context-pill">
        งาน <strong>{detail.jobTitle}</strong> · <strong>{detail.jobCode}</strong>
      </div>
      <div className="wx-tjj-kv-row">
        <span>นายจ้าง</span>
        <strong>{detail.employer?.employerName ?? detail.employerName ?? "-"}</strong>
      </div>
      <div className="wx-tjj-kv-row">

      </div>
      <div className="wx-tjj-kv-row">
        <span>จำนวนที่ต้องการ</span>
        <strong>{detail.requiredWorkerCount} อัตรา</strong>
      </div>
      <div className="wx-tjj-notice wx-tjj-notice--warn">{MARK_READY_WARNING}</div>

      {submitState.status === "error" && (
        <p className="wx-tjj-form-message" role="alert">
          {describeMarkReadyError(submitState.error)}
        </p>
      )}

      <div className="wx-tjj-drawer-foot">
        <button type="button" className="wx-button" onClick={onCancel} disabled={submitting}>
          ยกเลิก
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onConfirm} disabled={submitting}>
          {submitting ? "กำลังยืนยัน..." : "ยืนยัน"}
        </button>
      </div>
    </div>
  );
}

function CreateJobView({ titleId, form, onChange, onEmployerChange, employerOptions, employerDetail, onSkillChange, onAddSkillRow, onSubmit, onCancel, submitState }) {
  const submitting = submitState.status === "submitting";
  return (
    <div className="wx-tjj-drawer">
      <span className="wx-tjj-drawer-kicker">CREATE JOB</span>
      <h2 id={titleId} className="wx-tjj-drawer-title">
        สร้างงานใหม่
      </h2>
      <p className="wx-tjj-drawer-sub">บันทึกความต้องการแรงงาน (job demand) เพื่อรอ Matching ในขั้นตอนถัดไป</p>

      <form onSubmit={onSubmit}>
        <div className="wx-tjj-two">
          <div className="wx-tjj-field">
            <label>
              นายจ้าง <b>*</b>
            </label>
            <select value={form.employerId} onChange={(event) => onEmployerChange(event.target.value)} required>
              <option value="">เลือกนายจ้าง</option>
              {employerOptions.data.map((employer) => (
                <option key={employer.employerId} value={employer.employerId}>
                  {employer.legalName} · {employer.employerCode}
                </option>
              ))}
            </select>
          </div>
        </div>

        {form.employerId && (
          <div className="wx-tjj-context-pill">
            Agent เจ้าของ:{" "}
            <strong>
              {employerDetail.status === "loading"
                ? "กำลังโหลด..."
                : employerDetail.data
                  ? `${employerDetail.data.agent?.agentCode ?? "-"} · ${employerDetail.data.agent?.displayName ?? "-"}`
                  : "-"}
            </strong>
          </div>
        )}

        <div className="wx-tjj-field">
          <label>
            ชื่อตำแหน่งงาน (Job Title) <b>*</b>
          </label>
          <input type="text" placeholder="เช่น ช่างเย็บผ้าอุตสาหกรรม" value={form.jobTitle} onChange={(event) => onChange("jobTitle", event.target.value)} required />
        </div>

        <div className="wx-tjj-field">
          <label>รายละเอียดงาน</label>
          <textarea placeholder="ลักษณะงาน กะการทำงาน สวัสดิการเบื้องต้น" value={form.jobDescription} onChange={(event) => onChange("jobDescription", event.target.value)} />
        </div>

        <div className="wx-tjj-two">
          <div className="wx-tjj-field">
            <label>
              จำนวนที่ต้องการ (Required Worker Count) <b>*</b>
            </label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="เช่น 30"
              value={form.requiredWorkerCount}
              onChange={(event) => onChange("requiredWorkerCount", event.target.value)}
              required
            />
          </div>
          <div className="wx-tjj-field">
            <label>ภาษาที่ต้องการ</label>
            <input
              type="text"
              placeholder="เช่น ภาษาไทยเบื้องต้น"
              value={form.languageRequirementText}
              onChange={(event) => onChange("languageRequirementText", event.target.value)}
            />
          </div>
        </div>

        <div className="wx-tjj-two">
          <div className="wx-tjj-field">
            <label>วันที่เริ่มงาน (Start Date)</label>
            <input type="text" placeholder="YYYY-MM-DD" value={form.startDate} onChange={(event) => onChange("startDate", event.target.value)} />
          </div>
          <div className="wx-tjj-field">
            <label>วันที่สิ้นสุด (End Date)</label>
            <input type="text" placeholder="ไม่บังคับ" value={form.endDate} onChange={(event) => onChange("endDate", event.target.value)} />
          </div>
        </div>

        <div className="wx-tjj-two">
          <div className="wx-tjj-field">
            <label>ค่าจ้าง (Salary Amount)</label>
            <input type="text" inputMode="numeric" placeholder="เช่น 450" value={form.salaryAmount} onChange={(event) => onChange("salaryAmount", event.target.value)} />
          </div>
          <div className="wx-tjj-field">
            <label>สกุลเงิน (Salary Currency)</label>
            <input type="text" value={form.salaryCurrency} onChange={(event) => onChange("salaryCurrency", event.target.value)} />
          </div>
        </div>

        <div className="wx-tjj-field">
          <label>รอบการจ่าย (Salary Period)</label>
          <select value={form.salaryPeriod} onChange={(event) => onChange("salaryPeriod", event.target.value)}>
            {SALARY_PERIOD_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="wx-tjj-field">
          <label>สวัสดิการ (Benefits)</label>
          <textarea placeholder="เช่น ประกันสังคม ที่พัก อาหาร" value={form.benefitsText} onChange={(event) => onChange("benefitsText", event.target.value)} />
        </div>

        <div className="wx-tjj-field">
          <label>ทักษะที่ต้องการ (Skill Requirements)</label>
          {form.skillRequirements.map((row, index) => (
            <div className="wx-tjj-skill-req-row" key={index}>
              <input type="text" placeholder="รหัสทักษะ เช่น SEWING" value={row.skillCode} onChange={(event) => onSkillChange(index, "skillCode", event.target.value)} />
              <input
                type="text"
                inputMode="numeric"
                placeholder="จำนวนที่ต้องการ"
                value={row.requiredCount}
                onChange={(event) => onSkillChange(index, "requiredCount", event.target.value)}
              />
            </div>
          ))}
          <button type="button" className="wx-button wx-button--ghost wx-tjj-skill-req-add" onClick={onAddSkillRow}>
            + เพิ่มทักษะที่ต้องการ
          </button>
        </div>

        <div className="wx-tjj-notice wx-tjj-notice--warn">{CREATE_JOB_WARNING}</div>

        {submitState.status === "error" && (
          <p className="wx-tjj-form-message" role="alert">
            {describeCreateJobError(submitState.error)}
          </p>
        )}

        <div className="wx-tjj-drawer-foot">
          <button type="button" className="wx-button" onClick={onCancel}>
            ยกเลิก
          </button>
          <button type="submit" className="wx-button wx-button--primary" disabled={submitting}>
            {submitting ? "กำลังบันทึก..." : "สร้างงาน (Draft)"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditJobView({ titleId, job, form, onChange, onSkillChange, onAddSkillRow, onSubmit, onBack, submitState }) {
  const submitting = submitState.status === "submitting";
  return (
    <div className="wx-tjj-drawer">
      <span className="wx-tjj-drawer-kicker">EDIT JOB</span>
      <h2 id={titleId} className="wx-tjj-drawer-title">
        แก้ไขงาน
      </h2>
      <p className="wx-tjj-drawer-sub">แก้ไขเฉพาะข้อมูลความต้องการแรงงาน (demand data) ของงานนี้เท่านั้น</p>

      <form onSubmit={onSubmit}>
        <div className="wx-tjj-context-pill">
          กำลังแก้ไขงาน <strong>{job?.jobTitle}</strong> · {job?.jobCode}
        </div>
        <div className="wx-tjj-notice">{EDIT_JOB_NOTICE}</div>

        <div className="wx-tjj-two">
          <div className="wx-tjj-field">
            <label>นายจ้าง</label>
            <input type="text" value={`${job?.employer?.employerName ?? job?.employerName ?? "-"}`} disabled readOnly />
          </div>
        </div>

        <div className="wx-tjj-field">
          <label>
            ชื่อตำแหน่งงาน (Job Title) <b>*</b>
          </label>
          <input type="text" value={form.jobTitle} onChange={(event) => onChange("jobTitle", event.target.value)} required />
        </div>

        <div className="wx-tjj-field">
          <label>รายละเอียดงาน</label>
          <textarea value={form.jobDescription} onChange={(event) => onChange("jobDescription", event.target.value)} />
        </div>

        <div className="wx-tjj-two">
          <div className="wx-tjj-field">
            <label>
              จำนวนที่ต้องการ (Required Worker Count) <b>*</b>
            </label>
            <input type="text" inputMode="numeric" value={form.requiredWorkerCount} onChange={(event) => onChange("requiredWorkerCount", event.target.value)} required />
          </div>
          <div className="wx-tjj-field">
            <label>ภาษาที่ต้องการ</label>
            <input type="text" value={form.languageRequirementText} onChange={(event) => onChange("languageRequirementText", event.target.value)} />
          </div>
        </div>

        <div className="wx-tjj-two">
          <div className="wx-tjj-field">
            <label>วันที่เริ่มงาน (Start Date)</label>
            <input type="text" placeholder="YYYY-MM-DD" value={form.startDate ?? ""} onChange={(event) => onChange("startDate", event.target.value)} />
          </div>
          <div className="wx-tjj-field">
            <label>วันที่สิ้นสุด (End Date)</label>
            <input type="text" placeholder="ไม่บังคับ" value={form.endDate ?? ""} onChange={(event) => onChange("endDate", event.target.value)} />
          </div>
        </div>

        <div className="wx-tjj-two">
          <div className="wx-tjj-field">
            <label>ค่าจ้าง (Salary Amount)</label>
            <input type="text" inputMode="numeric" value={form.salaryAmount} onChange={(event) => onChange("salaryAmount", event.target.value)} />
          </div>
          <div className="wx-tjj-field">
            <label>สกุลเงิน (Salary Currency)</label>
            <input type="text" value={form.salaryCurrency} onChange={(event) => onChange("salaryCurrency", event.target.value)} />
          </div>
        </div>

        <div className="wx-tjj-field">
          <label>รอบการจ่าย (Salary Period)</label>
          <select value={form.salaryPeriod} onChange={(event) => onChange("salaryPeriod", event.target.value)}>
            {SALARY_PERIOD_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="wx-tjj-field">
          <label>สวัสดิการ (Benefits)</label>
          <textarea value={form.benefitsText} onChange={(event) => onChange("benefitsText", event.target.value)} />
        </div>

        <div className="wx-tjj-field">
          <label>ทักษะที่ต้องการ (Skill Requirements)</label>
          {form.skillRequirements.map((row, index) => (
            <div className="wx-tjj-skill-req-row" key={row.requirementId ?? index}>
              <input type="text" placeholder="รหัสทักษะ เช่น SEWING" value={row.skillCode} onChange={(event) => onSkillChange(index, "skillCode", event.target.value)} />
              <input
                type="text"
                inputMode="numeric"
                placeholder="จำนวนที่ต้องการ"
                value={row.requiredCount}
                onChange={(event) => onSkillChange(index, "requiredCount", event.target.value)}
              />
            </div>
          ))}
          <button type="button" className="wx-button wx-button--ghost wx-tjj-skill-req-add" onClick={onAddSkillRow}>
            + เพิ่มทักษะที่ต้องการ
          </button>
        </div>

        <div className="wx-tjj-notice wx-tjj-notice--warn">{CREATE_JOB_WARNING}</div>

        {submitState.status === "error" && (
          <p className="wx-tjj-form-message" role="alert">
            {describeEditJobError(submitState.error)}
          </p>
        )}

        <div className="wx-tjj-drawer-foot">
          <button type="button" className="wx-button" onClick={onBack}>
            ย้อนกลับ
          </button>
          <button type="submit" className="wx-button wx-button--primary" disabled={submitting}>
            {submitting ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
          </button>
        </div>
      </form>
    </div>
  );
}
